import { SolIcon } from '@sol/icons';
import { ObjectTools } from '../../tools/object-tools';
import { SolButtonType } from './sol-button';
import { SolText } from './sol-text';
import { SolBannerTools } from '../../tools/sol-banner-tools';

export type SolBannerMood = 'info' | 'success' | 'warning' | 'danger';

export type SolBannerPlacement = 'inline' | 'overlay';

export type SolBannerLevel = 'app' | 'page';

export interface SolBannerAction {
  readonly text: SolText;
  readonly icon?: SolIcon;
  readonly appendIcon?: SolIcon;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly ariaLabel?: SolText;
}

export class SolBanner {
  private constructor(
    public readonly mood: SolBannerMood,
    public readonly heading: SolText,
    public readonly description: SolText,
    public readonly action: SolBannerAction | undefined,
    public readonly secondaryAction: SolBannerAction | undefined,
    public readonly dismissible: boolean,
    public readonly rendered: boolean,
    public readonly icon: SolIcon,
    public readonly role: string,
    public readonly actionType: SolButtonType,
    public readonly secondaryActionType: SolButtonType,
    public readonly hostClass: string,
    public readonly hasActions: boolean,
  ) {}

  public static of(): SolBanner {
    return new SolBanner(
      'info',
      SolText.empty(),
      SolText.empty(),
      undefined,
      undefined,
      false,
      false,
      SolIcon.info,
      'status',
      SolButtonType.secondary,
      SolButtonType.transparent,
      '',
      false,
    ).recalculate();
  }

  public withMood(mood: SolBannerMood): SolBanner {
    return this.copy({ mood }).recalculate();
  }

  public withHeading(heading: SolText): SolBanner {
    return this.copy({ heading });
  }

  public withDescription(description: SolText): SolBanner {
    return this.copy({ description });
  }

  public withAction(action: SolBannerAction | undefined): SolBanner {
    return this.copy({ action: SolBannerTools.visibleAction(action) }).recalculate();
  }

  public withSecondaryAction(secondaryAction: SolBannerAction | undefined): SolBanner {
    return this.copy({ secondaryAction: SolBannerTools.visibleAction(secondaryAction) }).recalculate();
  }

  public withDismissible(dismissible: boolean): SolBanner {
    return this.copy({ dismissible }).recalculate();
  }

  public withRendered(rendered: boolean): SolBanner {
    return this.copy({ rendered }).recalculate();
  }

  private recalculate(): SolBanner {
    const icon = SolBannerTools.icon(this.mood);
    const role = SolBannerTools.role(this.mood);
    const actionType = SolBannerTools.actionType(true);
    const secondaryActionType = SolBannerTools.actionType(false);
    const hasActions = this.action !== undefined || this.secondaryAction !== undefined;
    const hostClass = SolBannerTools.hostClass(this.mood);
    return this.copy({ icon, role, actionType, secondaryActionType, hasActions, hostClass });
  }

  private copy(props: Partial<SolBanner>): SolBanner {
    return ObjectTools.copy<SolBanner>(this, props);
  }
}
